import React, { useEffect, useMemo, useRef, useState } from "react";
import DynamicForm from "../stories/Forms/DynamicForm";
import { loginSchema, vhLoginSchema } from "../stories/Forms/validationSchema";
import { loginFields, vhLoginFields } from "../stories/Forms/fields";
import { useAuth } from "../components/AuthContext/AuthContext";
import useApis from "../services/useAPI";
import { useNavigate } from "react-router";
import { loginUrl, ssoLoginUrl } from "../utils/constants";
import rememberMeCheckIcon from "../assets/icons/remember-me-check.svg";
import AccountCard from "../stories/Cards/AccountCard";
import { XCircleFill, CheckCircleFill} from "react-bootstrap-icons";
import { Spinner } from "react-bootstrap";
import Footer from "../components/Layout/Footer";

const REMEMBERED_EMAIL_KEY = "rememberedEmail";
const REMEMBER_ME_KEY = "rememberMe";

// Must match SSO_LOGIN_NOT_ALLOWED_MESSAGE in
// backend/phlebotomists_portal/utilities/constants.py
const SSO_LOGIN_BLOCKED_MESSAGE =
  "Your account is provisioned for SSO. Please sign in using SSO.";

// "Remember me" (persists the typed email locally, client-side only - there
// is no server-side "stay logged in longer" behaviour here) + "Forgot
// password?", rendered together as one row via vhLoginFields' password
// field `links` slot (Figma node 2079:7311 puts them side by side,
// directly under the password field).
const RememberMeRow = ({ checked, onChange }) => (
  <div className="remember-me-row">
    <label className="remember-me-checkbox">
      <input
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        type="checkbox"
      />
      <span aria-hidden="true" className="remember-me-box">
        <img alt="" className="remember-me-check-icon" src={rememberMeCheckIcon} />
      </span>
      Remember me
    </label>
    <a href="/forgot-password">Forgot password?</a>
  </div>
);

function LoginForm() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = useState(
    () =>
      localStorage.getItem(REMEMBER_ME_KEY) === "true" ||
      !!localStorage.getItem(REMEMBERED_EMAIL_KEY)
  );
  const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) || "";
  const credentialsRef = useRef(null);
  const pendingEmailRef = useRef("");
  const {
    callApi,
    serverError,
    isLoading,
    setIsLoading,
    resultPost,
    tokenError,
    setServerError
  } = useApis(loginUrl, "POST");

  const handleRememberMeChange = (checked) => {
    setRememberMe(checked);
  };

  const vhLoginFieldsWithRememberMe = useMemo(
    () =>
      vhLoginFields.map((field) =>
        field.name === "password"
          ? {
              ...field,
              links: (
                <RememberMeRow
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                />
              ),
            }
          : field
      ),
    [rememberMe]
  );

  const getUserRole = (userType) => {
    if (!userType) return "";
    if(userType === "healthcare-services"){
      return "healthCare";
    }
    else if(userType === "clinical-services"){
      return "clinical";
    }

  }


  const getDisplayError = (error) => {
    if (!error) return "";

    const lowerError = error.toLowerCase();

    if (lowerError.includes("unauthorised request") || 
        lowerError.includes("unauthorized request")) {
      return "It looks like you're trying to access the wrong portal.";
    }

    return error;
  };

  const isSsoLoginBlocked =
    serverError === SSO_LOGIN_BLOCKED_MESSAGE && !!auth?.featureFlag;

  useEffect(() => {
    if (isSsoLoginBlocked) {
      navigate("/account-access", { state: { ssoBlocked: true }, replace: true });
    }
  }, [isSsoLoginBlocked, navigate]);

  useEffect(() => {
    if (isSsoLoginBlocked) {
      return;
    }
    if ((serverError && !auth?.featureFlag) || tokenError) {
      const displayError = tokenError || serverError;
      const transformedError = getDisplayError(displayError);
      if(serverError === "unauthorised request"){
        setServerError("It looks like you're trying to access the wrong portal.")
      }
      auth?.showToast(
        "Error",
        transformedError,
        "../assets/mhc.png",
        {icon: <XCircleFill />, className: auth?.featureFlag && "vh-toaster"}
      );
    }
  }, [serverError, tokenError, isSsoLoginBlocked]);

  // Handle form submission
  const handleSubmit = async (data) => {
    const email = data.email.toLowerCase();
    const encodedPassword = btoa(data.password.trim());
    const payload = {
      email,
      password: encodedPassword,
    };
    pendingEmailRef.current = email;
    if (auth?.featureFlag) {
      credentialsRef.current = rememberMe
        ? { id: email, password: data.password.trim() }
        : null;
    }
    await callApi(payload);
  };

  // Effect to handle login result and navigation
  useEffect(() => {
    auth.setTitle("Log in");
    if (resultPost) {
      if (rememberMe && pendingEmailRef.current) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, pendingEmailRef.current);
        localStorage.setItem(REMEMBER_ME_KEY, "true");
      } else if (!rememberMe) {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
        localStorage.removeItem(REMEMBER_ME_KEY);
      }
      if (
        credentialsRef.current &&
        window.PasswordCredential &&
        navigator.credentials?.store
      ) {
        try {
          const cred = new window.PasswordCredential(credentialsRef.current);
          navigator.credentials.store(cred).catch(() => {});
        } catch (e) {
          // Handle error if needed
          }
      }
      credentialsRef.current = null;
      // change request for MFA disabled
      
      // if (resultPost?.mfa_enabled === false) {
        auth?.showToast(
          "Success",
          "Logged in successfully!",
          undefined,
          {icon: <CheckCircleFill />,
          className: auth?.featureFlag && "vh-toaster",}
        )
      // }
      // if (resultPost.mfa_enabled) {
      //   // Navigate to MFA setup if enabled
      //   navigate("/enable-webauthn");
      //   localStorage.setItem("mfa_enabled", resultPost.mfa_enabled);
      //   localStorage.setItem("user_id", resultPost.id);
      // } else {
        localStorage.setItem("isLoggedIn", true);
        localStorage.setItem("user_id", resultPost.id);
        if(resultPost){auth.login(resultPost)};
        localStorage.setItem("access_token", resultPost.access_token);
        localStorage.setItem("refresh_token", resultPost.refresh_token);
        sessionStorage.setItem("email", resultPost.email);
        localStorage.setItem("mfa_enabled", resultPost.mfa_enabled);
        localStorage.setItem("selectedService", getUserRole(resultPost.user_type));
        setIsLoading(true)
        setTimeout(() => {
                     // Navigate to patient record after login
                     {
                       !auth.featureFlagLoading && auth?.featureFlag
                         ? navigate("/store")
                         : navigate("/welcome");
                     }
         }, 1000);
      // }
    }
  }, [resultPost]);

  // Full-page navigation on purpose - this kicks off the SAML redirect
  // chain through the Boots IdP, not an API call this SPA can read a
  // response from.
  const handleSsoLogin = () => {
    window.location.assign(ssoLoginUrl);
  };

  if (auth.featureFlagLoading || isLoading || isSsoLoginBlocked)
      return (
        <div className="text-center">
          <Spinner
            animation="border"
            variant="info"
            className="my-custom-spinner"
          />
        </div>
      );

  return (
    <div className="login-wrapper">
      {!auth.featureFlagLoading && auth?.featureFlag ?
      
      <AccountCard title="Healthcare Professional Login" loginPage={true}>
        <div className="sso-login-screen">
          <button className="sso-login-btn" onClick={handleSsoLogin} type="button">
            Login with Boots SSO
          </button>

          <div className="sso-divider" role="separator" aria-label="or">
            <span>or</span>
          </div>

          <DynamicForm
            data={{ email: rememberedEmail }}
            fields={vhLoginFieldsWithRememberMe}
            isLoading={isLoading}
            onSubmit={handleSubmit}
            serverError={getDisplayError(serverError || tokenError)}
            validationSchema={vhLoginSchema}
          />
        </div>
      </AccountCard>
      
      :
      <>
      <AccountCard title="Log in" loginPage ={true}>
        <DynamicForm
          fields={loginFields}
          isLoading={isLoading}
          onSubmit={handleSubmit}
          serverError={getDisplayError(serverError || tokenError)}
          validationSchema={loginSchema}
        />
      </AccountCard>
      <Footer />
      </>
    }
    </div>
  );
}

export default LoginForm;
